import { useCallback, useContext, useEffect, useMemo, useState, createContext } from 'react'
// @ts-expect-error geoxp has no typedef
import * as GeoXp from '@mezzo-forte/geoxp'
import { useOnInit } from '../hooks/toolkit'
import { logger } from '../utils/helpers/logger'
import { EMPTY_GEOXP_CONFIG } from '../utils/constants/geoxp.constants'
import { removeManualCodeLibrarySuffix } from '../utils/helpers/agami.helpers'
import { ActiveAudio, GeoXpConfig, GeoXpPosition, Location, Position } from '../types/geoXp'
import { AgamiExperience } from '../types/supabase'

interface XpContextType {
  config: GeoXpConfig | null
  hasManualSpots: boolean
  positions: Position[]
  patterns: Position[]
  active: ActiveAudio[]
  location?: Location
  manualMode: boolean
  volume: number
  mute: boolean
  details: AgamiExperience | null
  loadExperience: (config: GeoXpConfig, details: AgamiExperience) => void
  setManualMode: (mode: boolean) => void
  setVolume: (volume: number) => void
  setMute: (mute: boolean) => void
  updateLocation: (location: { latitude: number; longitude: number }) => void
  unlock: () => void
  destroy: () => void
  start: () => void
  playManually: (code: string) => string | undefined
}

const XpContext = createContext<XpContextType>({
  config: null,
  hasManualSpots: false,
  positions: [],
  patterns: [],
  active: [],
  location: undefined,
  manualMode: false,
  volume: 1,
  mute: false,
  details: null,
  loadExperience: () => {},
  setManualMode: () => {},
  setVolume: () => {},
  setMute: () => {},
  updateLocation: () => {},
  unlock: () => {},
  destroy: () => {},
  start: () => {},
  playManually: () => undefined,
})

const useXp = () => useContext(XpContext)

interface XpContextProviderProps {
  children: React.ReactNode
}

const XpContextProvider: React.FC<XpContextProviderProps> = ({ children }) => {
  const init = useOnInit()

  const [details, setDetails] = useState<AgamiExperience | null>(null)
  const [config, setConfig] = useState<GeoXpConfig | null>(null)
  const [positions, setPositions] = useState<Position[]>([])
  const [active, setActive] = useState<ActiveAudio[]>([])
  const [location, setLocation] = useState<Location | undefined>()
  const [manualMode, setManualMode] = useState(false)
  const [volume, setVolume] = useState(1)
  const [mute, setMute] = useState(false)

  // singleton geoXp instance
  const geoXpInstance = useMemo(() => new GeoXp(EMPTY_GEOXP_CONFIG), [])

  // configuration
  const updatePositions = useCallback((configData: GeoXpConfig) => {
    if (!configData || !configData.experience || !configData.geo) return

    const configPositions: Position[] = configData.experience.patterns.map((pattern) => ({
      id: pattern.id,
      label: pattern.label,
      description: pattern.description,
      color: pattern.color,
      positions: pattern.spots
        .filter((spot) => spot.position)
        .map((spot) => {
          const geoPos = configData.geo.positions.find((pos) => spot.position === pos.id)
          if (!geoPos) return null
          return {
            radius: configData.geo.options.defaultRadius,
            deadband: configData.geo.options.defaultDeadband,
            label: spot.label ?? '',
            description: spot.description ?? '',
            ...configData.geo.positions.find((pos) => spot.position === pos.id),
          }
        })
        .filter((pos) => pos)
        .map((pos) => pos as GeoXpPosition),
    }))

    setPositions(configPositions)
  }, [])

  const patterns = useMemo(() => {
    if (!positions?.length) return []
    const global = positions.find((p) => p.id === 'default')
    return [global, ...positions.filter((p) => p.id !== 'default')] as Position[]
  }, [positions])

  const loadExperience = useCallback(
    (configData: GeoXpConfig, detailsData: AgamiExperience) => {
      if (!configData || !detailsData) return

      setConfig(configData)
      setDetails(detailsData)
      updatePositions(configData)
    },
    [updatePositions]
  )

  const initConfig = useCallback(() => {
    if (!geoXpInstance) return
    if (!config) return

    // reload geoxp config
    geoXpInstance.reload(config)

    // enable/disable internalGeolocation
    geoXpInstance.internalGeolocation(!manualMode)
  }, [geoXpInstance, config, manualMode])

  const hasManualSpots = useMemo(() => {
    if (!config) return false

    let manualSpot = false
    const configPatterns = config?.experience?.patterns || []
    configPatterns.forEach((p) => {
      if (p.spots.find((s) => s.code)) {
        manualSpot = true
      }
    })
    return manualSpot
  }, [config])

  // runtime
  const updateLocation = useCallback(
    ({ latitude, longitude }: { latitude: number; longitude: number }) => {
      if (!geoXpInstance) return
      if (!manualMode) return
      if (!latitude || !longitude) return

      geoXpInstance.updateGeolocation({
        coords: {
          latitude,
          longitude,
          accuracy: 1,
        },
      })
    },
    [geoXpInstance, manualMode]
  )

  const destroy = useCallback(() => {
    if (!geoXpInstance) return

    geoXpInstance.clearCookies()
    geoXpInstance.destroy()

    logger.debug('GeoXp instance destroyed', 'GeoXp')
  }, [geoXpInstance])

  const unlock = useCallback(() => {
    if (!geoXpInstance) return

    geoXpInstance.unlock()
  }, [geoXpInstance])

  const start = useCallback(() => {
    if (!geoXpInstance) return
    if (!config) return

    initConfig()
    unlock()
  }, [config, geoXpInstance, initConfig, unlock])

  const playManually = useCallback(
    (code: string) => {
      if (!geoXpInstance) return

      const configPatterns = config?.experience?.patterns || []

      /**
       * check with regex for spots that have ${code}§lib_<uuid>
       * (if library experience the will have it)
       * then, if multiple correspondance are found, notify user
       */
      const toPlayIds: string[] = configPatterns.reduce((acc: string[], curr) => {
        const found =
          curr.spots
            ?.filter((s) => s.code && removeManualCodeLibrarySuffix(s.code) === code)
            ?.map((s) => s.id) || []
        return [...acc, ...found]
      }, [])

      if (!toPlayIds || toPlayIds.length === 0) {
        return 'spot not found'
      }

      if (toPlayIds.length > 1) {
        return 'multiple spots found with this code'
        // TODO - show dialog and let user choose what to play!
      }

      return geoXpInstance.forceSpot(toPlayIds[0])
    },
    [config]
  )

  // add event handlers on init
  useEffect(() => {
    if (!init || !geoXpInstance) return

    // @ts-expect-error access to window property
    window.geoXp = geoXpInstance

    // geoXp events subscription
    geoXpInstance.on('active', (spotData: { id: string; label: string }) => {
      logger.debug(`Spot active: ${spotData.id} (${spotData.label})`, 'GeoXp')
    })

    geoXpInstance.on('outgoing', (spotData: { id: string; label: string }) => {
      logger.debug(`Spot outgoing: ${spotData.id} (${spotData.label})`, 'GeoXp')
    })

    geoXpInstance.on('visited', (spotData: { id: string; label: string }) => {
      logger.debug(`Spot visited: ${spotData.id} (${spotData.label})`, 'GeoXp')
    })

    geoXpInstance.on('play', (audioData: ActiveAudio) => {
      logger.debug(
        `Play audio: ${audioData.id} / spot ${audioData.spot.id} (${audioData.spot.label})`,
        'GeoXp'
      )
      setActive((prevActive) => {
        const prevIds = prevActive?.map((audio) => audio.id)
        return !prevIds.includes(audioData.id) ? [...prevActive, audioData] : prevActive
      })
    })

    geoXpInstance.on('stop', (audioData: ActiveAudio) => {
      logger.debug(
        `Stop audio: ${audioData.id} / spot ${audioData.spot.id} (${audioData.spot.label})`,
        'GeoXp'
      )
      setActive((prevActive) => prevActive?.filter((audio) => audio?.id !== audioData?.id))
    })

    geoXpInstance.on('position', (positionData: Location) => {
      setLocation(positionData)
    })
  }, [geoXpInstance, init])

  useEffect(() => {
    if (!geoXpInstance) return
    geoXpInstance.internalGeolocation(!manualMode)
  }, [manualMode, geoXpInstance])

  useEffect(() => {
    if (!geoXpInstance) return
    geoXpInstance.audio.setVolume(volume)
  }, [volume, geoXpInstance])

  useEffect(() => {
    if (!geoXpInstance) return

    if (mute) geoXpInstance.audio.setVolume(0)
    else geoXpInstance.audio.setVolume(volume)
  }, [mute, volume, geoXpInstance])

  const contextValue = useMemo(
    () => ({
      details,
      config,
      hasManualSpots,
      positions,
      patterns,
      active,
      location,
      manualMode,
      volume,
      mute,
      loadExperience,
      setManualMode,
      setVolume,
      setMute,
      updateLocation,
      unlock,
      destroy,
      start,
      playManually,
    }),
    [
      details,
      config,
      hasManualSpots,
      positions,
      active,
      location,
      manualMode,
      volume,
      mute,
      loadExperience,
      setManualMode,
      setVolume,
      setMute,
      updateLocation,
      unlock,
      destroy,
      start,
      playManually,
    ]
  )

  return <XpContext.Provider value={contextValue}>{children}</XpContext.Provider>
}

export { XpContextProvider, useXp }
